'use client';

import { useCallback, useEffect, useRef } from 'react';
import type { DesktopAppPlatform } from '@frameio/utils/browser';
import {
  Box,
  Button2,
  Dialog,
  Stack,
  Text,
  theme,
  useId,
} from '@frameio/vapor';
import { useIsIntegrationClient } from '../../contexts/ClientNameContext';
import { useOpenInBrowserMutation } from '../../integrations/gql';
import { useTrackUnsafe } from '../../segment/hooks';
import styles from './AppDownloadDialog.module.css';
import { getDriveAppDownloadUrl } from './constants';
import FrameLogo from './frameio-logo-white.svg';
import TEXT from './generated/TEXT/AppDownloadDialogContent';
import { useDesktopAppPlatform } from './hooks/useDesktopAppPlatform';

TEXT.DEFINE({
  /** Dialog title prompting the user to download the desktop app */
  title: 'Download Frame Drive...',
  /** Dialog description explaining why the app is needed */
  details: 'Mount projects on your desktop with Frame.io Drive',
  /** CTA to download the desktop app for macOS */
  downloadMac: 'Get macOS app',
  /** CTA to download the desktop app for Windows */
  downloadWindows: 'Get Windows app',
  /** Cancel / dismiss button */
  cancel: 'Close',
});

type AppDownloadDialogProps = {
  isShowing: boolean;
  onClose: () => void;
  projectId: string;
  /** Segment `source` used for the dialog's tracking events */
  trackingSource: string;
};

export function AppDownloadDialog({
  isShowing,
  onClose,
  projectId,
  trackingSource,
}: AppDownloadDialogProps) {
  const track = useTrackUnsafe();
  const titleId = useId();
  const initialFocusRef = useRef<HTMLButtonElement | null>(null);
  const detectedPlatform = useDesktopAppPlatform();

  useEffect(() => {
    if (!isShowing) return;
    track('drive-app-download-dialog-shown', {
      source: trackingSource,
      project_id: projectId,
      detected_platform: detectedPlatform,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowing]);
  const [openInBrowser] = useOpenInBrowserMutation({
    context: { resolveWithUXP: true },
  });
  const isIntegrationClient = useIsIntegrationClient();

  const handleDismiss = useCallback(() => {
    track('drive-app-download-dialog-dismissed', {
      source: trackingSource,
      project_id: projectId,
    });
    onClose();
  }, [onClose, projectId, track, trackingSource]);

  const handleDownload = useCallback(
    (key: DownloadOption['key'], link: DownloadOption['link']) => {
      track('drive-app-download-clicked', {
        platform: key,
        project_id: projectId,
        source: trackingSource,
      });
      if (isIntegrationClient) {
        openInBrowser({
          variables: {
            input: { url: link },
          },
        });
      } else {
        window.open(link, '_blank', 'noopener,noreferrer');
      }
      onClose();
    },
    [
      onClose,
      projectId,
      track,
      openInBrowser,
      isIntegrationClient,
      trackingSource,
    ]
  );

  const downloadOption = getDownloadOption(detectedPlatform);

  // note: z-index added to ensure dialog appears above other modals such as the
  // export location dialog (which may be above the export sequence dialog)
  return (
    <Dialog
      aria-labelledby={titleId}
      initialFocusRef={initialFocusRef}
      isShowing={isShowing}
      onRequestClose={handleDismiss}
      width="280px"
      zIndex={theme.zIndices.modal + 2}
    >
      <div className={styles.dialogContent}>
        {/* Frame.io logo */}
        <Box
          alignItems="center"
          className={styles.frameLogoContainer}
          display="flex"
          justifyContent="center"
        >
          <FrameLogo />
        </Box>

        {/* Title + description */}
        <Stack alignX="center" gap={8}>
          <Text
            color="primary"
            id={titleId}
            textAlign="center"
            variant="header"
          >
            {TEXT.title}
          </Text>
          <Text color="tertiary" textAlign="center" variant="body">
            {TEXT.details}
          </Text>
        </Stack>

        <div className={styles.buttonStack}>
          <Button2
            key={downloadOption.key}
            ref={initialFocusRef}
            block={true}
            onPress={() =>
              handleDownload(downloadOption.key, downloadOption.link)
            }
            variant={'primary'}
          >
            {downloadOption.label}
          </Button2>

          <Button2 block={true} onPress={handleDismiss} variant="tertiary">
            {TEXT.cancel}
          </Button2>
        </div>
      </div>
    </Dialog>
  );
}

type DownloadOption = {
  key: 'mac-arm' | 'mac-x64' | 'windows';
  label: string;
  link: string;
};

/**
 * Resolves the single download option for the detected platform, defaulting to
 * Apple Silicon macOS when the platform can't be determined.
 */
function getDownloadOption(platform: DesktopAppPlatform): DownloadOption {
  const key = platform ?? 'mac-arm';

  return {
    key,
    label: key === 'windows' ? TEXT.downloadWindows : TEXT.downloadMac,
    link: getDriveAppDownloadUrl(key),
  };
}
